import React from "react";

import get from "lodash/get";
import { injectIntl, IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/Accordion";
import Lunch from "@/icons/__generated__/Lunch/Lunch24";
import { IFAQKitchen, IFaqs } from "~/types/api-types";
import {
  IFoodAndBeverageRules,
  IFoodPrepRules,
  IKitchenAmenitiesProvided,
  IKitchenAmenitiesProvidedDesc,
  IKitchenSetupDesc,
  IKitchenSetups,
} from "~/types/local-types";

import {
  eventFaqDefinition,
  miscEventFaqDefinitions,
  category_labels,
  subtitles,
} from "../data/event-faq-definitions";
import {
  renderEntryTitle,
  kitchensSelected,
} from "../data/event-faq-display-sections";

import Description from "./Description";
import { analyticEventOnClick } from "./faq-utils";

interface FoodBeverageRulesProps {
  faqData: IFaqs;
  intl: IntlShape;
  userID: string;
  listingID: string;
}

const FoodBeverageRules: React.FC<FoodBeverageRulesProps> = ({
  faqData,
  intl,
  userID,
  listingID,
}) => {
  const eventFaqSelection = Object.keys(faqData);

  const selectedKitchenSetups = kitchensSelected(faqData);

  // Food beverage rules
  const foodBevProps =
    eventFaqDefinition.food_and_beverage["food_beverage_rules"][
      faqData["food_beverage_rules"] as keyof IFoodAndBeverageRules
    ];

  const foodAndBeverage = foodBevProps
    ? intl.formatMessage(foodBevProps)
    : faqData["food_beverage_rules"];

  const foodPrepProps =
    eventFaqDefinition.food_and_beverage["food_prep_rules"][
      faqData["food_prep_rules"] as keyof IFoodPrepRules
    ];

  const foodPrepRules = foodPrepProps
    ? intl.formatMessage(foodPrepProps)
    : faqData["food_prep_rules"];

  // Kitchen amenities
  const kitchenAmenitiesOptions = get(
    eventFaqDefinition,
    "food_and_beverage.kitchen_amenities_provided",
    {},
  ) as IKitchenAmenitiesProvided;
  const kitchenAmenityChoices: string[] = Object.keys(kitchenAmenitiesOptions);

  const kitchenAmenities = eventFaqSelection.reduce<string[]>((acc, key) => {
    if (kitchenAmenityChoices.includes(key)) {
      acc.push(key);
    }
    return acc;
  }, []);

  const label = intl.formatMessage(category_labels.food_and_beverage);

  return (
    <div className="EventFAQ">
      {faqData && (
        <Accordion
          collapsible
          type="single"
          onValueChange={() => {
            analyticEventOnClick(userID, listingID, "Food and Beverage");
          }}
        >
          <AccordionItem value="food-and-beverage-section">
            <AccordionTrigger className="tw-py-24">
              <div className="tw-flex tw-gap-12 tw-items-center">
                <Lunch />
                <Text element="h3" typeface="headline-1-m">
                  {label}
                </Text>
              </div>
            </AccordionTrigger>
            <AccordionContent className="tw-ml-36 tw-pb-24">
              {foodAndBeverage && (
                <>
                  <Text typeface="body-2-m" element="p">
                    {intl.formatMessage(miscEventFaqDefinitions.food_rules)}
                  </Text>
                  <Description description={foodAndBeverage} />
                  {(foodPrepRules ||
                    selectedKitchenSetups.length > 0 ||
                    kitchenAmenities.length > 0) && (
                    <hr className="tw-border-gray-200 tw-my-16" />
                  )}
                </>
              )}
              {foodPrepRules && (
                <>
                  <Text typeface="body-2-m" element="p">
                    {intl.formatMessage(
                      miscEventFaqDefinitions.food_prep_rules,
                    )}
                  </Text>
                  <Description description={foodPrepRules || ""} />
                  {(selectedKitchenSetups.length > 0 ||
                    kitchenAmenities.length > 0) && (
                    <hr className="tw-border-gray-200 tw-my-16" />
                  )}
                </>
              )}
              {selectedKitchenSetups.length > 0 && (
                <>
                  <div className="tw-space-y-8">
                    <Text typeface="body-2-m" element="p">
                      {intl.formatMessage(subtitles.available_kitchen_setups)}
                    </Text>
                    {selectedKitchenSetups.map((setup, key) => (
                      <div key={key}>
                        <Text>
                          {intl.formatMessage(
                            eventFaqDefinition.food_and_beverage
                              .kitchen_setups_provided[
                              setup as keyof IKitchenSetups
                            ],
                          )}
                        </Text>
                        {faqData[setup as keyof IKitchenSetupDesc]
                          ?.description && (
                          <Description
                            description={
                              faqData[setup as keyof IKitchenSetupDesc]
                                ?.description || ""
                            }
                            textSmall
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {kitchenAmenities.length > 0 && (
                    <hr className="tw-border-gray-200 tw-my-16" />
                  )}
                </>
              )}
              {kitchenAmenities.length > 0 && (
                <div className="tw-space-y-8">
                  <Text typeface="body-2-m">
                    {intl.formatMessage(subtitles.kitchen_amenities)}
                  </Text>
                  {kitchenAmenities.map((amenity, key) => (
                    <div key={key}>
                      {renderEntryTitle(
                        kitchenAmenitiesOptions[
                          amenity as keyof IKitchenAmenitiesProvided
                        ],
                        faqData[amenity as keyof IFAQKitchen]?.quantity,
                        amenity,
                      )}
                      {faqData[amenity as keyof IKitchenAmenitiesProvidedDesc]
                        ?.description && (
                        <Description
                          description={
                            faqData[
                              amenity as keyof IKitchenAmenitiesProvidedDesc
                            ]?.description || ""
                          }
                          textSmall
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
};

export default injectIntl(FoodBeverageRules);
